import { useEffect } from "react";
import _ from "lodash";
import Select from "react-select";
import Tooltip from "@/tooltip";
import {
	CalculatorIcon,
	ExclamationCircleIcon,
	LinkIcon,
	PencilSquareIcon,
	XMarkIcon,
} from "@heroicons/react/20/solid";
import { connect } from "react-redux";
import { Actions as ALLOWANCE_ACTIONS } from "../../store/actions/allowanceActions";
import { Actions as PAYROLL_ACTIONS } from "../../store/actions/payrollActions";
import { useMemo } from "react";
import {
	TagIcon,
	CalculatorIcon as CalculatorOutlineIcon,
	ClockIcon,
	CalendarDaysIcon,
} from "@/icons/outline";

const canLink = (allowance) => {
	const { method, salaryAllowanceAmount } = allowance;
	const linkableMethods = [
		"Cash",
		"Car Allowance",
		"Other",
		"Car FBV",
		"Deduction",
		"VOPS Employee",
		"VOPS Employer",
		"Director's Fees",
	];
	const isLinkable = salaryAllowanceAmount && method && linkableMethods.includes(method);
	return isLinkable;
};

const isBasicType = (allowance) => {
	const { allowanceType } = allowance;
	const isTypeBasic = ["277"].includes(String(allowanceType));
	return isTypeBasic;
};

const isBasic = (allowance) => {
	const { allowanceMethod } = allowance;
	const isMethodBasic = allowanceMethod?.toLowerCase() == "basic";
	return isBasicType(allowance) || isMethodBasic;
};
const numberFormatStyle = { minimumFractionDigits: 0, maximumFractionDigits: 2};

const AllowanceRow = ({
	allowance,
	employeeID,
	ready,
	salary,
	basicHours,
	unpaidHours,
	referencePeriodHours,
	payrollUpdateAllowance,
	payrollRemoveAllowance,
	processedTimesheets,
	currency,
	engaged,
	terminated,
	allowanceOptions,
	payPeriodDetails,
	alwaysExactHours,
	hasMultipleSalaries,
	exactOnTermination,
	toggleHolidayPayModal,
}) => {
	const previousTermination = terminated && salary.end <= payPeriodDetails.periodStart;
	const { allowanceType, salaryAllowance, salaryAllowanceAmount, leaveHours } = allowance;
	const linked = ["yes", "true"].includes(String(allowance.linked));
	const isLinkable = canLink(allowance);
	const isBasicAllowance = isBasic(allowance);

	const weeklyHours = parseFloat(salary.weeklyHours);
	const defaultHoursThisEmployee = weeklyHours ? (referencePeriodHours / 40) * weeklyHours : referencePeriodHours;

	const allowanceOption = allowanceOptions?.find(({ value }) => value == allowanceType);
	const allowanceMethod = allowance?.method || allowanceOption?.method;
	const allowanceName = allowanceOption?.label || allowance.name;
	const isHolidayPay = allowanceMethod === "HolidayPay";

	const canEditInput = useMemo(() => {
		const isBasicHours = isBasic(allowance);
		const canEditBasic = (exactOnTermination && (engaged || terminated)) || alwaysExactHours || salary.casual || hasMultipleSalaries;
		const isAllowanceSettingDisabled = allowanceOption?.disabled;
		const isGeneratedEntry = allowance.isStatutoryBonus || allowance.attachmentOfEarnings || allowance.isNetAdjustment;
		const isReady = ready;
		const wasLinked = isLinkable && linked;

		if (isBasicHours) return canEditBasic && !isReady;
		return !isAllowanceSettingDisabled && !isGeneratedEntry && !isReady && !wasLinked;
	}, [
		allowanceType,
		salaryAllowance,
		allowanceOption,
		terminated,
		engaged,
		isLinkable,
		linked,
		ready,
		alwaysExactHours,
		exactOnTermination,
	]);
	const isFixedAllowance = allowanceOption?.salaryAllowanceFixed;

	const canChangeAllowanceType = useMemo(() => {
		const isAllowanceFromSalary = salaryAllowance;
		const isBasicHours = isBasicType(allowance);
		const isAllowanceSettingDisabled = allowanceOption?.disabled;
		const isGeneratedEntry = allowance.isStatutoryBonus || allowance.attachmentOfEarnings || allowance.isNetAdjustment || allowance.isBackpay || allowance.isStatutoryPay;

		return !isAllowanceFromSalary && !isBasicHours && !isAllowanceSettingDisabled && !isHolidayPay && !isGeneratedEntry;
	}, [allowanceType, salaryAllowance, allowanceOption, allowanceMethod, isHolidayPay]);

	// if basic or unpaid calculate the ratio
	const ratio = useMemo(() => {
		let ratio = 1;

		if (basicHours || unpaidHours) {
			ratio = ((basicHours || defaultHoursThisEmployee) - (unpaidHours || 0)) / defaultHoursThisEmployee;
		} else if (previousTermination || (processedTimesheets && (terminated || engaged))) {
			ratio = 0;
		}
		if (ratio < 0.0001 && ratio > 0) ratio = 0;
		if (isFixedAllowance) ratio = 1;
		return ratio
	}, [basicHours, unpaidHours, processedTimesheets, terminated, engaged, previousTermination]);

	useEffect(() => {
		if (!allowance.disabled && !ready && allowance.isNew) {
			// this has been updated trigger a change of value
			payrollUpdateAllowance({
				...allowance,
				salary: salary.id,
				employeeID,
			});
		}
	}, []);

	useEffect(() => {
		if (isLinkable && linked && (!alwaysExactHours || terminated)) {
			const newInput = (salaryAllowanceAmount * ratio).toFixed(2);

			if (!allowance || allowance.input != newInput || allowance.salary != salary.id) {
				// this has been updated trigger a change of value
				payrollUpdateAllowance({
					...allowance,
					input: newInput,
					salary: salary.id,
					employeeID,
					// amount: e.target.value,
				});
			}
		}
	}, [basicHours, alwaysExactHours, terminated, unpaidHours]);

	const updateAllowance = (e) => {
		payrollUpdateAllowance({
			...allowance,
			input: e.target.value,
			salary: salary.id,
			employeeID,
			// amount: e.target.value,
		});
	};

	const updateAllowanceType = (selectedOption) => {
		payrollUpdateAllowance({
			...allowance,
			allowanceType: selectedOption.value,
			method: selectedOption.method,
			rate: selectedOption.rate,
			isOvertime: selectedOption.isOvertime || false,
			hours: selectedOption.method == "Hourly" ? 0 : null,
			salary: salary.id,
			employeeID,
		});
	};

	const [AllowanceIcon, input, amount] = useMemo(() => {
		let allowanceIcon = null;
		let input = allowance.input;
		let amount = (parseFloat(allowance.amount) || 0).toFixed(2);

		if (previousTermination && isBasicAllowance) {
			input = 0;
			amount = 0;
		}

		switch (allowanceMethod?.toLowerCase?.()) {
			case "hourly":
			case "basic":
				allowanceIcon = ClockIcon;
				break;
			case "multiplier":
				allowanceIcon = CalculatorOutlineIcon;
				break;
			case "commission":
				allowanceIcon = TagIcon;
				break;
			default:
				if (!input && salaryAllowanceAmount && linked) {
					input = (salaryAllowanceAmount * ratio).toFixed(2);
					amount = (salaryAllowanceAmount * ratio).toFixed(2);
					// isLinkedSalaryAllowance = true;
				}
		}

		return [allowanceIcon, input, amount];
	}, [
		allowanceMethod,
		allowance.input,
		allowance.amount,
		salaryAllowanceAmount,
		linked,
		ratio,
		previousTermination,
		isBasicAllowance,
	]);

	const inputGroupAddonStyle = {
		padding: "0 5px",
		border: "none",
		lineHeight: "38px",
		width: "29px",
		textAlign: "center",
		userSelect: "none",
	};

	const allowanceOptionsExcludingDisabled = allowanceOptions.filter(({ disabled }) => !disabled);

	return (
		<>
			<div
				style={{
					gridColumn: "span 4 / span 12",
					position: "relative",
					display: "flex",
					justifyContent: "center",
					alignContent: "center",
					flexDirection: "column",
				}}
			>
				{allowance.new && salaryAllowance && (
					<div
						style={{
							position: "absolute",
							left: -50,
							width: 20,
							lineHeight: "38px",
							textAlign: "center",
							cursor: "pointer",
						}}
						title="Remove this line"
						onClick={() =>
							payrollRemoveAllowance({
								id: allowance.id,
								employeeID,
							})
						}
					>
						<XMarkIcon className="h-6 w-6 mr-3 text-black" />
					</div>
				)}
				{allowance.updated && (
					<div
						style={{
							position: "absolute",
							left: -32,
							width: 20,
							lineHeight: "38px",
							textAlign: "center",
						}}
						title="Unsaved Changes for this line"
					>
						<ExclamationCircleIcon
							style={{ height: "1.5em", float: "left", marginRight: 5, color: "#ea611c" }}
						/>
					</div>
				)}

				{/* If there is a basic rate do not enable dropdown as it messes things up */}
				{canChangeAllowanceType && !allowance.basicRate ? (
					<Select
						options={allowanceOptionsExcludingDisabled}
						value={allowanceOption}
						onChange={updateAllowanceType}
						isDisabled={ready}
					/>
				) : (
					<>
						{allowanceName}
						{allowance.basicRate ? <span style={{fontSize: '9px'}}>Base Rate: {parseFloat(allowance.basicRate).toLocaleString(undefined,numberFormatStyle)}/hr</span> : ''}
					</>
				)}
			</div>

			{isHolidayPay ? (
				<div
					style={{
						gridColumn: "span 4 / span 12",
						lineHeight: "38px",
						display: "flex",
					}}
				>
					<div
						className="input-group"
						style={{ width: "50%" }}
					>
						<span
							className={`input-group-addon`}
							style={inputGroupAddonStyle}
						>
							<Tooltip content={"Hours on Leave"}>
								<CalendarDaysIcon style={{ lineHeight: "38px", verticalAlign: "sub" }} />
							</Tooltip>
						</span>
						<input
							style={{ height: "39px", minWidth: "30px", paddingLeft: 5 }}
							disabled={true}
							className="form-control"
							name="amount"
							type="text"
							placeholder=""
							value={leaveHours}
							onChange={(e) => {
								payrollUpdateAllowance({
									...allowance,
									leaveHours: e.target.value,
									salary: salary.id,
									employeeID,
								});
							}}
						/>
					</div>
					<div
						className="input-group"
						style={{ width: "50%" }}
					>
						<span
							className={`input-group-addon ${!ready ? "addon-primary" : ""}`}
							style={inputGroupAddonStyle}
						>
							<Tooltip
								content="Average Weekly Pay - Click to edit"
								isOpen={ready ? false : undefined}
								onClick={() => {
									if (ready) return;
									toggleHolidayPayModal(allowance, employeeID);
								}}
							>
								{AllowanceIcon ? (
									<AllowanceIcon
										style={{ width: "2rem", lineHeight: "38px", verticalAlign: "sub" }}
									/>
								) : (
									<span style={{ width: "100%", userSelect: "none" }}>{currency}</span>
								)}
							</Tooltip>
						</span>
						<input
							style={{ height: "39px", minWidth: "30px", paddingLeft: 5 }}
							disabled={true}
							className="form-control"
							name="amount"
							type="text"
							placeholder=""
							value={allowance.forceWeeklyPay || parseFloat(allowance.avgWeeklyPay || 0).toFixed(2)}
						/>
					</div>
				</div>
			) : (
				<div
					style={{
						gridColumn: `span 4 / span 12`,
						lineHeight: "38px",
					}}
				>
					<div className="input-group">
						<span
							className="input-group-addon"
							style={inputGroupAddonStyle}
						>
							{AllowanceIcon ? (
								<AllowanceIcon style={{ width: "2rem", lineHeight: "38px", verticalAlign: "sub" }} />
							) : (
								<span style={{ width: "100%", userSelect: "none" }}>{currency}</span>
							)}
						</span>
						<input
							style={{ height: "39px" }}
							disabled={!canEditInput}
							className="form-control"
							name="amount"
							type="text"
							placeholder=""
							value={input}
							onChange={updateAllowance}
						/>
						{isLinkable ? (
							<div
								onClick={() => {
									// setIsLinkedSalaryAllowance(!isLinkedSalaryAllowance)
									payrollUpdateAllowance({
										...allowance,
										linked: !allowance.linked || allowance.linked == "no" ? true : false,
										salary: salary.id,
										employeeID,
										// amount: e.target.value,
									});
								}}
								style={{
									position: "absolute",
									right: "5px",
									padding: "0",
									zIndex: 10,
									lineHeight: "38px",
									color: "black",
									background: "none",
									border: "none",
									cursor: "pointer",
								}}
								title="This allowance will auto-update based on the number of hours worked. Click to manually override."
							>
								{!ready &&
									(allowance.linked ? (
										<PencilSquareIcon
											style={{ verticalAlign: "middle" }}
											className="h-6 w-6"
										/>
									) : (
										<LinkIcon
											style={{ verticalAlign: "middle" }}
											className="h-6 w-6"
										/>
									))}
							</div>
						) : null}
					</div>
				</div>
			)}

			<div
				style={{
					gridColumn: "span 4 / span 12",
					lineHeight: "38px",
				}}
			>
				<div className="input-group">
					<span
						className="input-group-addon"
						style={inputGroupAddonStyle}
					>
						{currency}
					</span>
					<input
						style={{ height: "38px", paddingLeft: 0, paddingRight: 26 }}
						disabled={true}
						className="form-control"
						type="text"
						placeholder="0"
						value={isNaN(amount) ? "" : amount}
					/>
					{allowance.calculation ? (
						<div style={{ position: "absolute", cursor: "pointer", right: 0, zIndex: 10 }}>
							<Tooltip
								content={
									Array.isArray(allowance.calculation) ? (
										<div style={{ whiteSpace: "nowrap" }}>
											<p style={{ color: "white", fontWeight: 700 }}>
												{allowance.name} Explained
											</p>
											<ul style={{ whiteSpace: "nowrap", textAlign: "left" }}>
												{allowance.calculation.map((t) => (
													<li>{t}</li>
												))}
											</ul>
										</div>
									) : (
										allowance.calculation
									)
								}
								tagName="span"
							>
								<CalculatorIcon
									style={{ verticalAlign: "middle" }}
									className="h-7 w-7 mr-3"
								/>
							</Tooltip>
						</div>
					) : null}
				</div>
			</div>
		</>
	);
};

const mapStateToProps = (state) => {
	const { payroll, allowances, company } = state;
	const { settings: payrollSettings } = payroll || {};
	const { settings: companySettings } = company || {};
	const alwaysExactHours = ["true", "yes"].includes(String(payrollSettings?.alwaysExactHours));
	const exactOnTermination = ["true", "yes"].includes(String(payrollSettings?.exactOnTermination));

	const overtimeList = (state.company.settings.overtimes || []).map((i) => ({
		value: i.id,
		label: i.name,
		rate: i.rate,
		isOvertime: true,
		method: "Hourly",
	}));
	const allowanceList = [...(allowances?.allowanceList || []), ...(companySettings?.allowances || [])]
		.filter(({ temporary }) => !temporary)
		.map((i) => ({
			value: i.id,
			label: i.allowanceName || i.name,
			method: i.method,
			salaryAllowanceFixed: i.salaryAllowanceFixed,
			disabled: [277, 6558, 6559, 156766, 62402].includes(i.id), // make basic unpaid/half pay and net pay types disabled - and not selectable
		}));

	const governmentBonuses =
		payroll?.governmentBonuses?.map((i) => ({
			value: String(i.id),
			label: i.name,
			method: "cash",
			disabled: true,
		})) || [];
	const allowanceOptions = [...overtimeList, ...allowanceList, ...governmentBonuses].map((i) => ({
		...i,
	}));

	return {
		...payroll,
		alwaysExactHours,
		exactOnTermination,
		allowanceOptions,
		referencePeriodHours: state.payroll.payPeriodDetails.defaultHours,
		currency: state.company?.settings?.currency,
	};
};

const mapDispatchToProps = (dispatch) => ({
	allowanceGetList: () => dispatch(ALLOWANCE_ACTIONS.allowanceGetList()),
	payrollGetList: (payrunID) => dispatch(PAYROLL_ACTIONS.payrollGetList(payrunID)),
	payrollUpdateAllowance: (input) => dispatch(PAYROLL_ACTIONS.payrollUpdateAllowance(input)),
	payrollRemoveAllowance: (input) => dispatch(PAYROLL_ACTIONS.payrollRemoveAllowance(input)),
	toggleHolidayPayModal: (allowance, agreementId) =>
		dispatch(PAYROLL_ACTIONS.toggleHolidayPayModal({ status: true, allowance, agreementId })),
});

export default connect(mapStateToProps, mapDispatchToProps)(AllowanceRow);
